import React, { Component, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ContentProvider, useContent } from "@/context/ContentProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Admin from "@/pages/Admin";
import OrderSuccess from "@/pages/OrderSuccess";
import DynamicPage, { PageRenderer } from "@/pages/DynamicPage";
import LegalPage from "@/pages/LegalPage";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#faf7f2] p-8 text-center">
          <p className="font-serif-d text-3xl text-[#4b3a2f]">Algo salió mal</p>
          <button onClick={() => window.location.reload()} className="rounded-full bg-[#4b3a2f] px-8 py-3 text-sm text-[#f2e9dd]">
            Recargar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.lenis) window.lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const HomeSwitch = () => {
  const { homePage } = useContent();
  if (homePage) return <PageRenderer page={homePage} />;
  return <Home />;
};

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let lenis, raf;
    (async () => {
      try {
        const Lenis = (await import("lenis")).default;
        lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
        window.lenis = lenis;
        const loop = (t) => {
          lenis.raf(t);
          raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
      } catch (e) {
        /* native scrolling fallback */
      }
    })();
    return () => {
      cancelAnimationFrame(raf);
      lenis?.destroy();
      window.lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ContentProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Navbar />
              <main>
                <Routes>
                  <Route path="/" element={<HomeSwitch />} />
                  <Route path="/catalogo" element={<Catalog />} />
                  <Route path="/producto/:id" element={<ProductDetail />} />
                  <Route path="/carrito" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/pedido/:id" element={<OrderSuccess />} />
                  <Route path="/iniciar-sesion" element={<Login />} />
                  <Route path="/registrarse" element={<Register />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/p/:slug" element={<DynamicPage />} />
                  <Route path="/terminos" element={<LegalPage type="terms" />} />
                  <Route path="/privacidad" element={<LegalPage type="privacy" />} />
                </Routes>
              </main>
              <Footer />
            </BrowserRouter>
            <Toaster
              position="top-center"
              richColors
              toastOptions={{
                style: { background: "#4b3a2f", color: "#f2e9dd", border: "1px solid rgba(201,162,75,0.4)" },
              }}
            />
          </CartProvider>
        </ContentProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
